import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { paymentRequestAPI, commissionAPI } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../utils/i18n';
import { formatCurrency } from '../../utils/currency';
import { Send, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useDialog } from '../../components/Dialog';
import PageHeader from './components/PageHeader';
import './PaymentRequestPage.css';

const PaymentRequestPage = () => {
  const navigate = useNavigate();
  const { language } = useAuth();
  const { t } = useTranslation(language);
  const { showWarning, showSuccess, showError, showConfirm, DialogComponent } = useDialog();
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [currentCommission, setCurrentCommission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [formData, setFormData] = useState({
    requestedAmount: '',
    bankName: '',
    accountNumber: '',
    accountHolderName: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [requestsRes, commissionRes] = await Promise.all([
        paymentRequestAPI.getAll(),
        commissionAPI.getDetails(new Date().getMonth() + 1, new Date().getFullYear()),
      ]);

      console.log('Payment Request - Commission API response:', commissionRes.data);
      console.log('Payment Request - Requests API response:', requestsRes.data);

      // Fix nested data structure for requests
      const requestsData = requestsRes.data?.data || requestsRes.data || [];
      setPaymentRequests(Array.isArray(requestsData) ? requestsData : []);

      // Fix nested data structure: commissionRes.data.data
      const commissionData = commissionRes.data.data || commissionRes.data;

      // Calculate available balance by subtracting paid and pending requests for current month
      const currentMonth = new Date().getMonth() + 1;
      const currentYear = new Date().getFullYear();

      const totalPaid = requestsData
        .filter(req => req.month === currentMonth && req.year === currentYear && req.status === 'paid')
        .reduce((sum, req) => sum + parseFloat(req.amount || 0), 0);

      const totalPending = requestsData
        .filter(req => req.month === currentMonth && req.year === currentYear && ['pending', 'approved'].includes(req.status))
        .reduce((sum, req) => sum + parseFloat(req.amount || 0), 0);

      // Available balance = Net total - Paid - Pending
      const availableBalance = (commissionData?.netTotal || 0) - totalPaid - totalPending;

      setCurrentCommission({
        ...commissionData,
        totalPaid,
        totalPending,
        availableBalance,
      });

      console.log('Payment Request - Current commission:', commissionData);
      console.log('Payment Request - Available balance:', availableBalance);
      console.log('Payment Request - Requests:', requestsData);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      console.error('Error details:', error.response?.data || error.message);
      // Mock data
      setCurrentCommission({
        netTotal: 1460000,
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
      });
      setPaymentRequests([
        {
          id: 1,
          amount: 2500000,
          month: 10,
          year: 2024,
          status: 'approved',
          createdAt: '2024-10-25T10:00:00Z',
          approvedAt: '2024-10-26T14:30:00Z',
        },
        {
          id: 2,
          amount: 1800000,
          month: 9,
          year: 2024,
          status: 'pending',
          createdAt: '2024-09-28T09:15:00Z',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRequestForm = () => {
    if (!currentCommission || currentCommission.availableBalance <= 0) {
      showWarning(t('noCommissionToRequest'));
      return;
    }

    // Navigate to create payment request page
    navigate('/loan/payment-request/create');
  };

  const handleCloseRequestForm = () => {
    setShowRequestForm(false);
    setFormData({
      requestedAmount: '',
      bankName: '',
      accountNumber: '',
      accountHolderName: '',
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();

    // Validate form
    if (!formData.requestedAmount || !formData.bankName || !formData.accountNumber || !formData.accountHolderName) {
      showWarning(t('pleaseCompleteAllFields'));
      return;
    }

    // Validate requested amount
    const requestedAmount = parseFloat(formData.requestedAmount);
    if (requestedAmount <= 0 || requestedAmount > currentCommission.availableBalance) {
      showWarning(t('invalidWithdrawalAmount'));
      return;
    }

    setSubmitting(true);

    try {
      await paymentRequestAPI.create({
        amount: requestedAmount,
        month: currentCommission.month,
        year: currentCommission.year,
        bankName: formData.bankName,
        accountNumber: formData.accountNumber,
        accountHolderName: formData.accountHolderName,
      });

      showSuccess(t('requestSuccess'));
      handleCloseRequestForm();
      setTimeout(() => fetchData(), 1500); // Refresh
    } catch (error) {
      console.error('Failed to create payment request:', error);
      showError(error.response?.data?.message || t('errorOccurred'));
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(language === 'vi' ? 'vi-VN' : language === 'ko' ? 'ko-KR' : 'en-US');
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      pending: { label: t('pending'), icon: Clock, color: 'warning' },
      approved: { label: t('approved'), icon: CheckCircle, color: 'success' },
      rejected: { label: t('rejected'), icon: XCircle, color: 'danger' },
      paid: { label: t('paid'), icon: CheckCircle, color: 'success' },
    };
    return statusMap[status] || statusMap.pending;
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="payment-request-page">
      <DialogComponent />
      <PageHeader title={t('paymentRequestTitle')} backTo="/loan/commission" />

      {/* Request Form Modal */}
      {showRequestForm && (
        <div className="modal-overlay" onClick={handleCloseRequestForm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t('createPaymentRequest')}</h2>
              <button className="btn-close" onClick={handleCloseRequestForm}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmitRequest} className="request-form">
              <div className="form-group">
                <label className="form-label">{t('totalAvailable')}</label>
                <div className="amount-display" style={{
                  background: '#f0fdf4',
                  padding: '0.75rem',
                  borderRadius: '0.5rem',
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  color: '#166534'
                }}>
                  {formatCurrency(currentCommission?.availableBalance || 0)}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">{t('requestedAmount')}</label>
                <input
                  type="number"
                  name="requestedAmount"
                  className="form-input"
                  placeholder={t('enterRequestedAmount')}
                  value={formData.requestedAmount}
                  onChange={handleInputChange}
                  max={currentCommission?.availableBalance || 0}
                  min={0}
                  step="1000"
                  required
                />
                <small style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                  {t('maxAmount')}: {formatCurrency(currentCommission?.availableBalance || 0)}
                </small>
              </div>

              <div className="form-group">
                <label className="form-label">{t('bankName')}</label>
                <input
                  type="text"
                  name="bankName"
                  className="form-input"
                  placeholder={t('enterBankName')}
                  value={formData.bankName}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">{t('accountNumber')}</label>
                <input
                  type="text"
                  name="accountNumber"
                  className="form-input"
                  placeholder={t('enterAccountNumber')}
                  value={formData.accountNumber}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">{t('accountHolderName')}</label>
                <input
                  type="text"
                  name="accountHolderName"
                  className="form-input"
                  placeholder={t('enterAccountHolderName')}
                  value={formData.accountHolderName}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseRequestForm}
                  disabled={submitting}
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <span className="spinner-sm"></span>
                      {t('sending')}
                    </>
                  ) : (
                    t('submitRequest')
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="page-content">
        {/* Current Month Commission */}
        {currentCommission && (
          <div className="current-commission-card">
            <div className="card-header">
              <h2 className="card-title">
                {t('currentMonthCommission')} {currentCommission.month}/{currentCommission.year}
              </h2>
            </div>

            <div className="amount-display">
              <span className="amount-label">{t('totalReceivable')}:</span>
              <span className="amount-value">{formatCurrency(currentCommission.availableBalance)}</span>
            </div>

            {currentCommission.availableBalance > 0 ? (
              <button
                className="btn btn-primary btn-lg btn-block"
                onClick={handleOpenRequestForm}
                disabled={submitting}
              >
                <Send size={20} />
                {t('createPaymentRequest')}
              </button>
            ) : (
              <div className="info-message">
                <AlertCircle size={20} />
                <p>
                  {currentCommission.availableBalance === 0
                    ? t('noCommissionYet')
                    : t('negativeCommission')}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Payment Request History */}
        <div className="history-section">
          <h2 className="section-title">{t('requestHistory')}</h2>

          {paymentRequests.length === 0 ? (
            <div className="empty-state-small">
              {t('noRequestsYet')}
            </div>
          ) : (
            <div className="payment-request-list">
              {paymentRequests.map((request) => {
                const statusInfo = getStatusInfo(request.status);
                const Icon = statusInfo.icon;

                return (
                  <div key={request.id} className="payment-request-item">
                    <div className="request-header">
                      <div className="request-period">
                        {t('month')} {request.month}/{request.year}
                      </div>
                      <span className={`status-badge ${statusInfo.color}`}>
                        <Icon size={14} />
                        {statusInfo.label}
                      </span>
                    </div>

                    <div className="request-amount">
                      {formatCurrency(request.amount)}
                    </div>

                    <div className="request-footer">
                      <div className="request-date">
                        <span className="date-label">{t('requestCreated')}:</span>
                        <span className="date-value">{formatDate(request.createdAt)}</span>
                      </div>

                      {request.approvedAt && (
                        <div className="request-date">
                          <span className="date-label">{t('requestApproved')}:</span>
                          <span className="date-value">{formatDate(request.approvedAt)}</span>
                        </div>
                      )}

                      {request.paidAt && (
                        <div className="request-date">
                          <span className="date-label">{t('requestPaid')}:</span>
                          <span className="date-value">{formatDate(request.paidAt)}</span>
                        </div>
                      )}

                      {request.rejectionReason && (
                        <div className="rejection-info">
                          <strong>{t('rejectionReason')}:</strong>
                          <p>{request.rejectionReason}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="info-box">
          <h3 className="info-title">💡 {t('note')}</h3>
          <ul className="info-list">
            <li>{t('noteItem1')}</li>
            <li>{t('noteItem2')}</li>
            <li>{t('noteItem3')}</li>
            <li>{t('noteItem4')}</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PaymentRequestPage;
